/**
 * BANDEJA "NECESITA REVISIÓN" (FASE 5.5)
 *
 * F14.2 — también para el OPERARIO (sus propias acciones): mismo contenido, sin los ejecutores (los pone el
 * servidor) y con «habla con tu encargado» donde a un encargado se le diría «consultor». Cada tarjeta cuenta qué
 * intentaste, qué encontró el servidor y qué puedes hacer.
 *
 * UX dedicada para que un ENCARGADO entienda y resuelva, uno a uno, cada
 * conflicto offline detectado por FASE 5.3/5.4 (`estadoSync: 'EN_ESPERA'`)
 * y cada mutación que el servidor rechazó (`RECHAZADA`) — nunca un botón
 * "forzar", nunca sobrescribir el servidor con el snapshot local (encargo
 * §31): el servidor sigue siendo la autoridad; esta pantalla decide qué
 * hacer con la INTENCIÓN local.
 *
 * TRES SECCIONES, NUNCA MEZCLADAS (§2/§17)
 *
 *   · Conflictos de revisión — la línea/NC cambió de verdad mientras el
 *     dispositivo estaba sin conexión (`razonEspera: 'CONFLICTO_REVISION'`).
 *   · Bloqueadas por otra acción — dependían de una mutación que nunca se
 *     aplicó (`razonEspera: 'DEPENDENCIA_BLOQUEADA'`); se resuelven siempre
 *     a través de esa mutación padre, nunca por su cuenta.
 *   · No se pudieron aplicar — RECHAZADA por una regla de negocio o pérdida
 *     de ámbito/permiso; nunca ofrece "reintentar sobre estado actual".
 */
import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';

import { Cargando, fechaHora, Vacio } from '../componentes/base.tsx';
import { Shell } from '../componentes/shell.tsx';
import { useSesionOfflineActiva } from '../offline/contextoPerfil.tsx';
import {
  evaluarReintento,
  estadoLocalConocido,
  type EvaluacionReintento,
} from '../offline/conflictos.ts';
import {
  descartarMutacionRevision,
  leerLineaLocal,
  listarMutacionesEnEspera,
  listarMutacionesRechazadas,
  listarOtsLocal,
  mutacionesDependientesDe,
  reintentarMutacionEnEspera,
  type LineaOfflineLocal,
  type Mutacion,
  type OtOfflineLocal,
  type PayloadLineaConfirmar,
  type PayloadLineaMotivo,
  type PayloadLineaVerificar,
  type PayloadMutacion,
  type PayloadNcCerrar,
  type PayloadNcIniciarTratamiento,
  type TipoMutacion,
} from '../offline/db.ts';
import { rotuloOperacion } from '../offline/operaciones.ts';
import { sincronizarPendientes } from '../offline/sync.ts';
import { useConectividad } from '../pwa.ts';

const ETIQUETA_ACCION: Record<TipoMutacion, string> = {
  LINEA_CONFIRMAR: 'Confirmar la línea',
  LINEA_VERIFICAR: 'Verificar la línea',
  LINEA_NO_OK: 'Marcar la línea como No OK',
  LINEA_NO_APLICA: 'Marcar la línea como No aplica',
  NC_CREAR: 'Abrir una no conformidad',
  NC_INICIAR_TRATAMIENTO: 'Iniciar tratamiento de la no conformidad',
  NC_CERRAR: 'Cerrar la no conformidad',
  EVIDENCIA_SUBIR: 'Adjuntar una evidencia',
  MNT_INICIAR: 'Iniciar la intervención',
  MNT_NOTA: 'Añadir una nota a la intervención',
  MNT_CONSUMO: 'Registrar un consumo de material',
  MNT_FINALIZAR: 'Finalizar la intervención',
};

/** F14.3 — una operación del técnico: su conflicto se explica con lo que dijo el servidor, nunca con revisiones. */
const esDeMantenimiento = (m: Mutacion): boolean =>
  m.tipo.startsWith('MNT_') || (m.tipo === 'EVIDENCIA_SUBIR' && (m.payload as { ambito?: string }).ambito === 'PT');

function isoParaInputLocal(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number): string => String(n).padStart(2, '0');
  return `${String(d.getFullYear())}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function inputLocalAIso(valor: string, respaldo: string): string {
  if (valor.trim() === '') return respaldo;
  const d = new Date(valor);
  return Number.isNaN(d.getTime()) ? respaldo : d.toISOString();
}

/** Observación/motivo, tal como ya iban en el payload original — texto llano, nunca reescrito a la fuerza (§22). */
function textoPayload(m: Mutacion): { observacion: string | null; motivo: string | null } {
  const p = m.payload as { observacion?: string | null; motivo?: string | null };
  return { observacion: p.observacion ?? null, motivo: p.motivo ?? null };
}

/**
 * Formulario de reintento — reconstruye el payload EDITABLE a partir del
 * original (§20/§21/§22: ejecutores/fechaEfectiva/observación preservados,
 * nunca reescritos en silencio) y crea la mutación NUEVA al confirmar.
 */
function FormularioReintento({
  idPerfil,
  m,
  evaluacion,
  onResuelto,
  propia,
}: {
  idPerfil: string;
  m: Mutacion;
  evaluacion: Extract<EvaluacionReintento, { retryable: true }>;
  onResuelto: () => void;
  /** F14.2 — acción del propio operario: el ejecutor lo pone el servidor, no se elige aquí. */
  propia: boolean;
}): ReactNode {
  const original = textoPayload(m);
  const [linea, setLinea] = useState<LineaOfflineLocal | null>(null);
  const [fechaEfectiva, setFechaEfectiva] = useState(() => isoParaInputLocal(m.fechaEfectiva));
  const [observacion, setObservacion] = useState(original.observacion ?? '');
  const [motivo, setMotivo] = useState(original.motivo ?? '');
  const [accionCorrectiva, setAccionCorrectiva] = useState(
    m.tipo === 'NC_CERRAR' ? (m.payload as PayloadNcCerrar).accionCorrectiva : '',
  );
  const [ejecutores, setEjecutores] = useState<string[]>(
    m.tipo === 'LINEA_CONFIRMAR' ? [...(m.payload as PayloadLineaConfirmar).ejecutoresReales] : [],
  );
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (m.tipo !== 'LINEA_CONFIRMAR' || propia) return;
    void leerLineaLocal(idPerfil, m.idEntidad).then(setLinea);
  }, [idPerfil, m.idEntidad, m.tipo, propia]);

  async function confirmar(): Promise<void> {
    if (m.tipo === 'LINEA_CONFIRMAR' && !propia && ejecutores.length === 0) {
      setError('Selecciona al menos un ejecutor real.');
      return;
    }
    if ((m.tipo === 'LINEA_NO_OK' || m.tipo === 'LINEA_NO_APLICA') && motivo.trim() === '') {
      setError('El motivo es obligatorio.');
      return;
    }
    if (m.tipo === 'NC_CERRAR' && accionCorrectiva.trim() === '') {
      setError('La acción correctiva es obligatoria.');
      return;
    }
    const fechaIso = inputLocalAIso(fechaEfectiva, m.fechaEfectiva);
    const obs = observacion.trim() === '' ? null : observacion.trim();
    let payload: PayloadMutacion;
    if (m.tipo === 'LINEA_CONFIRMAR') {
      payload = { fechaEfectiva: fechaIso, observacion: obs, ejecutoresReales: ejecutores } satisfies PayloadLineaConfirmar;
    } else if (m.tipo === 'LINEA_VERIFICAR') {
      payload = { fechaEfectiva: fechaIso, observacion: obs } satisfies PayloadLineaVerificar;
    } else if (m.tipo === 'LINEA_NO_OK' || m.tipo === 'LINEA_NO_APLICA') {
      payload = { fechaEfectiva: fechaIso, observacion: obs, motivo: motivo.trim() } satisfies PayloadLineaMotivo;
    } else if (m.tipo === 'NC_INICIAR_TRATAMIENTO') {
      payload = { motivo: motivo.trim() === '' ? null : motivo.trim() } satisfies PayloadNcIniciarTratamiento;
    } else {
      payload = { accionCorrectiva: accionCorrectiva.trim() } satisfies PayloadNcCerrar;
    }

    setGuardando(true);
    setError(null);
    const revisionBase = evaluacion.ambito === 'LINEA' ? evaluacion.estadoFresco.revisionActual : evaluacion.estadoFresco.revisionActual;
    const resultado = await reintentarMutacionEnEspera(idPerfil, m.idOperacionCliente, {
      payload,
      revisionBase,
      fechaEfectiva: fechaIso,
    });
    setGuardando(false);
    if (resultado === null) {
      setError('Esta acción ya no está pendiente de revisión — puede que se haya resuelto desde otra pestaña.');
      return;
    }
    //  Mismo criterio que `crearMutacionLinea`/`crearMutacionNcCrear`
    //  (`offline/mutaciones.ts`): encolar es SIEMPRE lo primero (ya
    //  ocurrió, arriba) y esto nunca lo sustituye — sin esperar, la nueva
    //  mutación (y cualquier dependiente re-encadenado a PENDIENTE) no
    //  necesita que el usuario recargue o vuelva a pulsar "Sincronizar".
    if (typeof navigator !== 'undefined' && navigator.onLine) {
      void sincronizarPendientes(idPerfil);
    }
    onResuelto();
  }

  return (
    //  FASE 5.7-C C05 — `min(420px, 90vw)`, no `min(420px, 100%)`: un
    //  ancho máximo fijo mayor que el viewport de un móvil dejaba este
    //  formulario más ancho que la pantalla, y `100%` no basta cuando el
    //  contenedor real es más ancho que el viewport (mismo hallazgo que
    //  en `offline.tsx`, ver el comentario en `FormularioEvidenciaOffline`).
    <div className="datos" style={{ maxWidth: 'min(420px, 90vw)', marginTop: 8 }}>
      {m.tipo === 'LINEA_CONFIRMAR' && !propia && (
        <div className="campo">
          <label>Ejecutores reales</label>
          {linea === null ? (
            <p className="suave pequena">Cargando…</p>
          ) : linea.asignados.length === 0 ? (
            <p className="suave pequena">Sin operarios asignados en el paquete local.</p>
          ) : (
            linea.asignados.map((a) => (
              <label key={a.idOperario} className="fila" style={{ gap: 6, alignItems: 'center' }}>
                <input
                  type="checkbox"
                  checked={ejecutores.includes(a.idOperario)}
                  onChange={(e) => {
                    setEjecutores((prev) => (e.target.checked ? [...prev, a.idOperario] : prev.filter((id) => id !== a.idOperario)));
                  }}
                />
                {a.codigo}
              </label>
            ))
          )}
        </div>
      )}
      {(m.tipo === 'LINEA_NO_OK' || m.tipo === 'LINEA_NO_APLICA') && (
        <div className="campo">
          <label htmlFor={`revision-motivo-${m.idOperacionCliente}`}>Motivo</label>
          <textarea id={`revision-motivo-${m.idOperacionCliente}`} value={motivo} onChange={(e) => setMotivo(e.target.value)} />
        </div>
      )}
      {m.tipo === 'NC_INICIAR_TRATAMIENTO' && (
        <div className="campo">
          <label htmlFor={`revision-motivo-nc-${m.idOperacionCliente}`}>Motivo (opcional)</label>
          <textarea id={`revision-motivo-nc-${m.idOperacionCliente}`} value={motivo} onChange={(e) => setMotivo(e.target.value)} />
        </div>
      )}
      {m.tipo === 'NC_CERRAR' && (
        <div className="campo">
          <label htmlFor={`revision-accion-${m.idOperacionCliente}`}>Acción correctiva</label>
          <textarea id={`revision-accion-${m.idOperacionCliente}`} value={accionCorrectiva} onChange={(e) => setAccionCorrectiva(e.target.value)} />
        </div>
      )}
      {(m.tipo === 'LINEA_CONFIRMAR' || m.tipo === 'LINEA_VERIFICAR' || m.tipo === 'LINEA_NO_OK' || m.tipo === 'LINEA_NO_APLICA') && (
        <div className="campo">
          <label htmlFor={`revision-obs-${m.idOperacionCliente}`}>Observaciones (opcional)</label>
          <textarea id={`revision-obs-${m.idOperacionCliente}`} value={observacion} onChange={(e) => setObservacion(e.target.value)} />
        </div>
      )}
      <div className="campo">
        <label htmlFor={`revision-fecha-${m.idOperacionCliente}`}>Fecha efectiva</label>
        <input
          id={`revision-fecha-${m.idOperacionCliente}`}
          type="datetime-local"
          value={fechaEfectiva}
          onChange={(e) => setFechaEfectiva(e.target.value)}
        />
        <p className="suave pequena" style={{ margin: '4px 0 0' }}>
          La que registraste sin conexión. El servidor vuelve a aplicar sus reglas y tolerancias sobre la que confirmes aquí.
        </p>
      </div>
      {error !== null && <p className="nota error" role="alert">{error}</p>}
      <div className="fila" style={{ gap: 8 }}>
        <button type="button" className="principal" disabled={guardando} onClick={() => void confirmar()}>
          {guardando ? 'Enviando…' : 'Confirmar y reintentar'}
        </button>
      </div>
    </div>
  );
}

/**
 * §4 del PRE-COMMIT REVIEW de cierre — el copy de la cascada de descarte
 * debe ser INEQUÍVOCO sobre qué se pierde, en particular el archivo local
 * de una evidencia: "también se descartarán N cambios" no basta, porque no
 * deja claro que uno de ellos es un fichero que deja de existir. Evidencia
 * y el resto de dependientes se anuncian por separado, con su propio texto.
 */
function TextoCascadaDescarte({ dependientes }: { dependientes: readonly Mutacion[] }): ReactNode {
  const evidencias = dependientes.filter((d) => d.tipo === 'EVIDENCIA_SUBIR');
  const otros = dependientes.filter((d) => d.tipo !== 'EVIDENCIA_SUBIR');
  return (
    <>
      {evidencias.length > 0 && (
        <p className="pequena" style={{ margin: '6px 0 0' }}>
          También se descartará{evidencias.length === 1 ? '' : 'n'} {evidencias.length}{' '}
          {evidencias.length === 1 ? 'evidencia pendiente asociada' : 'evidencias pendientes asociadas'}. El archivo local{' '}
          {evidencias.length === 1 ? 'dejará' : 'dejarán'} de conservarse.
        </p>
      )}
      {otros.length > 0 && (
        <p className="pequena" style={{ margin: '6px 0 0' }}>
          También se descartarán {otros.length} cambio(s) que dependían de éste: {otros.map((d) => ETIQUETA_ACCION[d.tipo]).join(', ')}.
        </p>
      )}
    </>
  );
}

/** F14.2 — «qué hago ahora», en palabras de quien lo hizo. */
function queHacer(m: Mutacion, operario: boolean): string {
  if (m.razonEspera === 'DEPENDENCIA_BLOQUEADA') return 'Resuelve primero la acción de la que depende; esta seguirá después.';
  //  F14.3 — nunca se reaplica a ciegas: el técnico mira el parte como está ahora y, si sigue teniendo sentido, lo
  //  vuelve a registrar desde allí (p. ej. otro consumo con el stock real).
  if (esDeMantenimiento(m)) {
    return 'Abre el parte en «Mis partes» con conexión para ver cómo está ahora. Si todavía hace falta, vuelve a registrarlo desde allí; después descarta este cambio local.';
  }
  if (m.estadoSync === 'RECHAZADA') {
    return operario
      ? 'Comprueba la tarea en «Mis trabajos». Si no sabes qué hacer, habla con tu encargado. Puedes descartar este cambio local: no se enviará.'
      : 'Comprueba la orden de trabajo con conexión y decide si repetir la acción o descartar este cambio local.';
  }
  return operario
    ? 'Pulsa «Revisar» para ver el estado actual. Si todavía tiene sentido, puedes volver a intentarlo; si no, descarta el cambio o habla con tu encargado.'
    : 'Pulsa «Revisar» para ver el estado actual y decide si reintentar o descartar.';
}

function TarjetaMutacionEnRevision({
  idPerfil,
  m,
  ot,
  onCambio,
  operario,
}: {
  idPerfil: string;
  m: Mutacion;
  ot: OtOfflineLocal | undefined;
  onCambio: () => void;
  operario: boolean;
}): ReactNode {
  const conectividad = useConectividad();
  const [expandido, setExpandido] = useState(false);
  const [evaluacion, setEvaluacion] = useState<EvaluacionReintento | null>(null);
  const [evaluando, setEvaluando] = useState(false);
  const [dependientes, setDependientes] = useState<Mutacion[] | null>(null);
  const [confirmandoDescarte, setConfirmandoDescarte] = useState(false);
  const [descartando, setDescartando] = useState(false);
  const esConflicto = m.razonEspera === 'CONFLICTO_REVISION';
  const esBloqueada = m.razonEspera === 'DEPENDENCIA_BLOQUEADA';
  const esRechazada = m.estadoSync === 'RECHAZADA';

  async function revisar(): Promise<void> {
    setExpandido(true);
    if (!esConflicto || esDeMantenimiento(m) || conectividad === 'OFFLINE') return;
    setEvaluando(true);
    try {
      setEvaluacion(await evaluarReintento(idPerfil, m));
    } catch {
      setEvaluacion({ retryable: false, motivo: 'No se ha podido consultar el estado actual del servidor.' });
    } finally {
      setEvaluando(false);
    }
  }

  async function pedirDescarte(): Promise<void> {
    setDependientes(await mutacionesDependientesDe(idPerfil, m.idOperacionCliente));
    setConfirmandoDescarte(true);
  }

  async function confirmarDescarte(): Promise<void> {
    setDescartando(true);
    await descartarMutacionRevision(idPerfil, m.idOperacionCliente);
    setDescartando(false);
    setConfirmandoDescarte(false);
    onCambio();
  }

  return (
    <li className="datos" style={{ border: '1px solid var(--linea, #ddd)', borderRadius: 8, padding: 12 }}>
      <div className="fila" style={{ justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 8 }}>
        <div>
          <div className="fila" style={{ gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            {ot !== undefined && <Link to={operario ? `/mis-ots/${ot.id}` : `/offline/${ot.id}`} className="tarjeta-ot-codigo">{ot.codigo}</Link>}
            {esDeMantenimiento(m) && <Link to={`/mantenimiento/mis-partes/${m.idOt}`} className="tarjeta-ot-codigo">Ver el parte</Link>}
            <strong>{m.rotuloLocal != null ? rotuloOperacion(m) : ETIQUETA_ACCION[m.tipo]}</strong>
          </div>
          <p className="suave pequena" style={{ margin: '4px 0 0' }}>
            Registrado sin conexión el {fechaHora(m.creadoLocalEn)}.
          </p>
        </div>
      </div>

      {esConflicto && esDeMantenimiento(m) && (
        <p className="pequena" style={{ margin: '8px 0 0' }}>
          <span className="suave">Mientras el dispositivo estaba sin conexión, esto cambió:</span> {m.ultimoError?.mensaje ?? 'el parte ya no está como lo dejaste.'}
        </p>
      )}
      {esConflicto && !esDeMantenimiento(m) && (
        <p className="pequena" style={{ margin: '8px 0 0' }}>
          Mientras el dispositivo estaba sin conexión, esto cambió. Tu copia: <strong>{estadoLocalConocido(m)}</strong>
          {' · '}Servidor ahora: <strong>{m.detalleConflicto?.estadoServidorActual ?? '—'}</strong>.
        </p>
      )}
      {esBloqueada && (
        <p className="pequena" style={{ margin: '8px 0 0' }}>
          Depende de otra acción que no llegó a aplicarse en el servidor. Resuélvela primero desde su propia tarjeta.
        </p>
      )}
      {esRechazada && (
        <p className="pequena" style={{ margin: '8px 0 0' }}>
          <span className="suave">Lo que encontró el servidor:</span> {m.ultimoError?.mensaje ?? 'El servidor no ha podido aplicar esta acción.'}
        </p>
      )}
      <p className="pequena" style={{ margin: '6px 0 0' }}>
        <span className="suave">Qué hacer:</span> {queHacer(m, operario)}
      </p>

      {expandido && esConflicto && !esDeMantenimiento(m) && (
        <p className="suave pequena" style={{ margin: '6px 0 0' }}>
          Revisión local: {m.revisionBase ?? '—'} · Revisión servidor: {m.detalleConflicto?.revisionServidorActual ?? '—'}
        </p>
      )}

      <div className="fila" style={{ gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
        {!expandido && <button type="button" className="pequeno" onClick={() => void revisar()}>Revisar</button>}
        {!confirmandoDescarte && (
          <button type="button" className="pequeno" onClick={() => void pedirDescarte()}>Descartar cambio local</button>
        )}
      </div>

      {confirmandoDescarte && (
        <div className="nota" style={{ marginTop: 10 }}>
          <p style={{ margin: 0 }}>Este cambio no se enviará al servidor.</p>
          {dependientes !== null && dependientes.length > 0 && <TextoCascadaDescarte dependientes={dependientes} />}
          <div className="fila" style={{ gap: 8, marginTop: 8 }}>
            <button type="button" className="principal" disabled={descartando} onClick={() => void confirmarDescarte()}>
              {descartando ? 'Descartando…' : 'Confirmar descarte'}
            </button>
            <button type="button" className="pequeno" onClick={() => setConfirmandoDescarte(false)}>Cancelar</button>
          </div>
        </div>
      )}

      {expandido && esConflicto && !esDeMantenimiento(m) && (
        <div style={{ marginTop: 10 }}>
          {conectividad === 'OFFLINE' ? (
            <p className="suave pequena">Necesitas conexión para consultar el estado actual.</p>
          ) : evaluando ? (
            <p className="suave pequena">Consultando estado actual del servidor…</p>
          ) : evaluacion?.retryable === true ? (
            <FormularioReintento idPerfil={idPerfil} m={m} evaluacion={evaluacion} onResuelto={onCambio} propia={operario} />
          ) : (
            <p className="nota" style={{ margin: 0 }}>
              {operario ? 'Ya no se puede repetir tal cual. Habla con tu encargado; puedes descartar este cambio local.' : 'Requiere revisión de un consultor.'}
            </p>
          )}
        </div>
      )}
    </li>
  );
}

/**
 * FASE 5.7-A §9/§33 — mismo criterio que `paginas/offline.tsx`: sin
 * `catch`, un fallo de lectura local (corrupción, `ErrorDescifrado`) dejaba
 * `enEspera` en `null` para siempre y esta pantalla —precisamente la que
 * se visita cuando algo YA va mal— se quedaba en `<Cargando/>` eterno, sin
 * ningún mensaje. Nunca el error técnico crudo como mensaje principal.
 */
const MENSAJE_FALLO_LECTURA_LOCAL =
  'No se han podido leer los datos guardados en este dispositivo. Puede que estén dañados — vuelve a preparar los datos sin conexión la próxima vez que tengas conexión.';

export function PaginaOfflineRevision(): ReactNode {
  const { perfilId, roles } = useSesionOfflineActiva();
  const [enEspera, setEnEspera] = useState<Mutacion[] | null>(null);
  const [rechazadas, setRechazadas] = useState<Mutacion[]>([]);
  const [ots, setOts] = useState<OtOfflineLocal[]>([]);
  const [recarga, setRecarga] = useState(0);
  const [errorLectura, setErrorLectura] = useState<string | null>(null);

  const cargar = useCallback(async (idPerfil: string) => {
    try {
      const [espera, rech, listadoOts] = await Promise.all([
        listarMutacionesEnEspera(idPerfil),
        listarMutacionesRechazadas(idPerfil),
        listarOtsLocal(idPerfil),
      ]);
      setErrorLectura(null);
      setEnEspera(espera);
      setRechazadas(rech);
      setOts(listadoOts);
    } catch {
      setErrorLectura(MENSAJE_FALLO_LECTURA_LOCAL);
    }
  }, []);

  useEffect(() => {
    void cargar(perfilId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [perfilId, cargar, recarga]);

  const esEncargado = roles.some((r) => r.rol === 'ENCARGADO');
  //  F14.3 — el técnico revisa aquí lo suyo con el mismo criterio que el operario (su propio trabajo).
  const tecnico = !esEncargado && roles.some((r) => r.rol === 'TECNICO_MANTENIMIENTO');
  const operario = !esEncargado && (tecnico || roles.some((r) => r.rol === 'OPERARIO'));
  if (!esEncargado && !operario) {
    return (
      <Shell titulo="Necesita revisión" migas={[{ texto: 'Trabajar sin conexión', a: '/offline' }, { texto: 'Necesita revisión' }]}>
        <Vacio titulo="Esta bandeja no está disponible para tu perfil" />
      </Shell>
    );
  }
  const migas = tecnico
    ? [{ texto: 'Mis partes', a: '/mantenimiento/mis-partes' }, { texto: 'Necesita revisión' }]
    : operario
    ? [{ texto: 'Mis trabajos', a: '/mis-ots' }, { texto: 'Necesita revisión' }]
    : [{ texto: 'Trabajar sin conexión', a: '/offline' }, { texto: 'Necesita revisión' }];

  const otPorId = new Map(ots.map((o) => [o.id, o] as const));
  const onCambio = (): void => setRecarga((n) => n + 1);
  const conflictos = (enEspera ?? []).filter((m) => m.razonEspera === 'CONFLICTO_REVISION');
  const bloqueadas = (enEspera ?? []).filter((m) => m.razonEspera !== 'CONFLICTO_REVISION');

  return (
    <Shell titulo="Necesita revisión" migas={migas}>
      {errorLectura !== null ? (
        <Vacio titulo={errorLectura} />
      ) : enEspera === null ? (
        <Cargando />
      ) : (
        <>
          <div className="panel">
            <div className="panel-cabecera"><h2>Conflictos de revisión</h2></div>
            <div className="panel-cuerpo sin-relleno">
              {conflictos.length === 0 ? (
                <Vacio titulo="Sin conflictos pendientes de revisión" />
              ) : (
                <ul style={{ display: 'flex', flexDirection: 'column', gap: 10, margin: 0, padding: 16, listStyle: 'none' }}>
                  {conflictos.map((m) => (
                    <TarjetaMutacionEnRevision key={m.idOperacionCliente} idPerfil={perfilId} m={m} ot={otPorId.get(m.idOt)} onCambio={onCambio} operario={operario} />
                  ))}
                </ul>
              )}
            </div>
          </div>

          {bloqueadas.length > 0 && (
            <div className="panel">
              <div className="panel-cabecera"><h2>Bloqueadas por otra acción</h2></div>
              <div className="panel-cuerpo sin-relleno">
                <ul style={{ display: 'flex', flexDirection: 'column', gap: 10, margin: 0, padding: 16, listStyle: 'none' }}>
                  {bloqueadas.map((m) => (
                    <TarjetaMutacionEnRevision key={m.idOperacionCliente} idPerfil={perfilId} m={m} ot={otPorId.get(m.idOt)} onCambio={onCambio} operario={operario} />
                  ))}
                </ul>
              </div>
            </div>
          )}

          <div className="panel">
            <div className="panel-cabecera"><h2>No se pudieron aplicar</h2></div>
            <div className="panel-cuerpo sin-relleno">
              {rechazadas.length === 0 ? (
                <Vacio titulo="Sin acciones rechazadas" />
              ) : (
                <ul style={{ display: 'flex', flexDirection: 'column', gap: 10, margin: 0, padding: 16, listStyle: 'none' }}>
                  {rechazadas.map((m) => (
                    <TarjetaMutacionEnRevision key={m.idOperacionCliente} idPerfil={perfilId} m={m} ot={otPorId.get(m.idOt)} onCambio={onCambio} operario={operario} />
                  ))}
                </ul>
              )}
            </div>
          </div>
        </>
      )}
    </Shell>
  );
}
